import { Outlet, NavLink, useNavigate, useLocation, useParams } from "react-router-dom";
import { useAuth } from "../App";
import { useProject } from "../context/ProjectContext";
import { useState, useEffect } from "react";
import {
  LayoutDashboard,
  Users,
  CalendarDays,
  Table2,
  Timer,
  Clock,
  Menu,
  X,
  LogOut,
  ChevronDown,
  ListTodo,
  FileText,
  Home,
  FolderOpen,
  UserCircle,
  PieChart,
  Receipt,
  Calculator,
  Settings,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Button } from "./ui/button";
import { colors } from "./landing/colors";
import { BUILD_LABEL, BUILD_VERSION, BUILD_DATE, BUILD_COMMIT } from "../buildInfo";
import ChangelogModal from "./ChangelogModal";
import AppBackground from "./AppBackground";
import { getVisibleProjectPages, buildProjectPageUrl } from "../config/projectNav";

const API = '/api';

export function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== "undefined" ? window.innerWidth >= 1024 : true
  );
  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return isDesktop;
}

// ============ PROJECT SUB-NAVIGATION ============
function ProjectSubNav({ projectId, activeProject, projects: contextProjects, onSelectProject, user }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [localProjects, setLocalProjects] = useState([]);

  useEffect(() => {
    const loadProjects = async () => {
      if (contextProjects && contextProjects.length > 0) {
        setLocalProjects(contextProjects);
      } else {
        try {
          const token = localStorage.getItem("token");
          if (!token) return;
          const res = await fetch(`${API}/projects`, {
            headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" }
          });
          if (res.ok) setLocalProjects(await res.json());
        } catch (e) {}
      }
    };
    loadProjects();
  }, [contextProjects]);

  const projects = localProjects.length > 0 ? localProjects : contextProjects;
  // Tabs aus der zentralen Config — Single Source of Truth für Labels
  const subNavItems = getVisibleProjectPages(user?.role)
    .map((p) => ({
      to: buildProjectPageUrl(p.key, projectId),
      icon: p.icon,
      label: p.shortLabel || p.label, // kompakter in Subnav-Bar
      testId: `subnav-${p.testKey}`,
    }))
    // Globale Einsatzplanung blenden wir hier aus (im Layout-Header verfügbar) —
    // wäre sonst redundant: der Link führt aus dem Projektkontext heraus.
    .filter((it) => !it.to.startsWith("/app/einsatzplanung"));
  const isActive = (path) => location.pathname === path;

  return (
    <div style={{ backgroundColor: "var(--tga-border)", borderBottom: "1px solid var(--tga-border)" }}>
      <div className="px-8">
        <div className="flex items-center gap-4 py-2">
          <div className="flex items-center gap-2 pr-4 border-r border-white/10">
            <FolderOpen className="w-4 h-4 text-white/30" />
            <span className="text-[11px] font-semibold text-white/30 uppercase tracking-wider">Projekt</span>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 px-3 py-1.5 border border-white/10 hover:bg-white/5 transition-colors rounded" data-testid="project-selector">
                <div className="w-2.5 h-2.5 rounded-full ring-2 ring-white/20" style={{ backgroundColor: activeProject?.color || "#6B7280" }} />
                <span className="font-medium text-sm text-white max-w-[200px] truncate">{activeProject?.name || "Projekt wählen"}</span>
                <ChevronDown className="w-3.5 h-3.5 text-white/40" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-72">
              <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase">Projekt wechseln</div>
              <DropdownMenuSeparator />
              {projects.filter(p => p.is_active).map((project) => (
                <DropdownMenuItem key={project.id} onClick={() => onSelectProject(project)} className={project.id === activeProject?.id ? "bg-gray-100" : ""}>
                  <div className="flex items-center gap-3 w-full">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: project.color || "#6B7280" }} />
                    <span className="truncate">{project.name}</span>
                    {project.id === activeProject?.id && <span className="ml-auto text-xs text-green-500 font-medium">Aktiv</span>}
                  </div>
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/app")}>
                <LayoutDashboard className="w-4 h-4 mr-2" />
                Zu den Projekten
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="h-5 w-px bg-white/8" />

          <nav className="flex items-center gap-0.5">
            {subNavItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={`flex items-center gap-2 px-3 py-1.5 text-[13px] font-medium rounded transition-all ${
                  isActive(item.to)
                    ? "bg-white/10 text-white"
                    : "text-white/40 hover:bg-white/5 hover:text-white/70"
                }`}
                data-testid={item.testId}
              >
                <item.icon className="w-3.5 h-3.5" />
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/app")}
              className="bg-transparent border-white/10 text-white/40 hover:bg-white/5 hover:text-white/70 text-xs h-7"
            >
              <Home className="w-3.5 h-3.5 mr-1.5" />
              Projekte
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============ MAIN LAYOUT ============
export default function Layout() {
  const { user, logout } = useAuth();
  const { activeProject, projects, selectProject } = useProject();
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId } = useParams();
  const [menuOpen, setMenuOpen] = useState(false);
  const [changelogOpen, setChangelogOpen] = useState(false);
  const isDesktop = useIsDesktop();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const showProjectSubNav = isDesktop && location.pathname.startsWith("/app/projekte/") && projectId;
  const currentProjectId = projectId || activeProject?.id;

  const focusRoutes = ["/app/stempeluhr", "/app/tagesuebersicht"];
  const isFocusRoute = focusRoutes.some(r => location.pathname === r);

  const globalNavItems = [
    { to: "/app/mitarbeiter", icon: Users, label: "Mitarbeiter", testId: "nav-mitarbeiter", adminOnly: true },
    { to: "/app/einsatzplanung", icon: CalendarDays, label: "Einsatzplanung", testId: "nav-einsatzplanung" },
    { to: "/app/stundenkonten", icon: Table2, label: user?.role === "admin" || user?.role === "projektleiter" ? "Stundenkonten" : "Mein Stundenkonto", testId: "nav-stundenkonten" },
    { to: "/app/abrechnungen", icon: Receipt, label: "Abrechnungen", testId: "nav-abrechnungen", adminOnly: true },
    { to: "/app/stempeluhr", icon: Timer, label: "Stempeluhr", testId: "nav-stempeluhr" },
  ];

  const visibleNavItems = globalNavItems.filter(item => {
    if (item.adminOnly && !user?.permissions?.can_manage_users) return false;
    return true;
  });

  const getRoleBadge = (role) => {
    const badges = { admin: "Admin", projektleiter: "Projektleiter", mitarbeiter: "Mitarbeiter", nachunternehmer: "Nachunternehmer" };
    return badges[role] || role;
  };

  return (
    <div className="dark-app min-h-screen flex flex-col font-['Inter',system-ui,sans-serif] relative" style={{ backgroundColor: "transparent" }}>
      <AppBackground />

      {/* ============ WHITE BRANDING HEADER ============ */}
      <div className="sticky top-0 z-50">
        <header style={{ backgroundColor: "#ffffff", height: "72px" }} data-testid="app-header">
          <div className="flex items-center justify-between px-8 h-full">
            <div className="flex items-center cursor-pointer" onClick={() => navigate("/app")} data-testid="app-logo-link">
              <img
                src="https://customer-assets.emergentagent.com/job_8871d808-9ccd-4157-8de6-34b5e308c5cc/artifacts/vzg0ky1i_tgaone_black.png.png"
                alt="TGAone"
                style={{ height: "52px", width: "auto" }}
              />
            </div>

            {/* User Menu */}
            <div className="flex items-center gap-3">
              {/* Admin-Versionsanzeige (nur für Admins sichtbar) — klickbar -> Changelog */}
              {user?.role === "admin" && (
                <button
                  type="button"
                  onClick={() => setChangelogOpen(true)}
                  className="hidden md:flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-blue-50 hover:bg-blue-100 border border-blue-200 text-blue-700 font-mono text-[10px] leading-none cursor-pointer transition-colors"
                  title={`Klick: Was wurde geändert\n\n${BUILD_LABEL}\nVersion ${BUILD_VERSION}\nCommit ${BUILD_COMMIT}`}
                  data-testid="admin-build-badge"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                  <span className="font-bold" data-testid="build-label">{BUILD_LABEL}</span>
                </button>
              )}

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 px-3 py-1.5 rounded hover:bg-gray-50 transition-colors" data-testid="user-menu">
                    <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center">
                      <UserCircle className="w-4 h-4 text-gray-400" />
                    </div>
                    <span className="hidden md:inline text-[13px] font-medium text-gray-700">{user?.name}</span>
                    <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                    <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded bg-gray-100 text-gray-600">{getRoleBadge(user?.role)}</span>
                  </div>
                  {user?.role === "admin" && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate("/app/app-einstellungen")} data-testid="menu-app-settings">
                        <Settings className="w-4 h-4 mr-2" />
                        App-Einstellungen
                      </DropdownMenuItem>
                      <div className="px-3 py-2 text-[10px] font-mono text-gray-500 leading-relaxed" data-testid="admin-build-info">
                        <button
                          type="button"
                          onClick={() => setChangelogOpen(true)}
                          className="w-full flex justify-between items-center hover:text-blue-700 cursor-pointer mb-0.5"
                          data-testid="admin-build-info-changelog"
                        >
                          <span>Build</span>
                          <span className="font-bold text-blue-700 underline-offset-2 hover:underline">{BUILD_LABEL}</span>
                        </button>
                        <div className="flex justify-between"><span>Version</span><span>{BUILD_VERSION}</span></div>
                        <div className="flex justify-between"><span>Commit</span><span>{BUILD_COMMIT}</span></div>
                      </div>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600 focus:text-red-600" data-testid="logout-btn">
                    <LogOut className="w-4 h-4 mr-2" />
                    Abmelden
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <button className="lg:hidden p-2 text-gray-500 hover:text-gray-900" onClick={() => setMenuOpen(!menuOpen)}>
                {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </header>

        {/* ============ DARK APP NAVIGATION ============ */}
        <nav className="hidden lg:block" style={{ backgroundColor: colors.darkBlue, borderBottom: "1px solid var(--tga-border)" }} data-testid="app-global-nav">
          <div className="flex items-center gap-0.5 px-8 py-1">
            <NavLink
              to="/app" end
              className={({ isActive }) =>
                `flex items-center gap-2 px-4 py-2 rounded text-[13px] font-medium transition-all ${
                  isActive ? "bg-white/10 text-white" : "text-white/40 hover:text-white/70 hover:bg-white/5"
                }`
              }
              data-testid="nav-dashboard"
            >
              <LayoutDashboard className="w-4 h-4" />
              Projekte
            </NavLink>
            {visibleNavItems.map((item) => (
              <NavLink
                key={item.to} to={item.to}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-4 py-2 rounded text-[13px] font-medium transition-all ${
                    isActive ? "bg-white/10 text-white" : "text-white/40 hover:text-white/70 hover:bg-white/5"
                  }`
                }
                data-testid={item.testId}
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </NavLink>
            ))}
          </div>
        </nav>

        {/* Mobile Navigation */}
        {menuOpen && !isDesktop && (
          <div className="lg:hidden" style={{ backgroundColor: colors.anthracite, borderBottom: "1px solid var(--tga-border)" }}>
            <div className="px-4 py-2">
              <nav className="flex flex-col gap-0.5">
                <NavLink
                  to="/app" end onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-3 rounded ${isActive ? "bg-white/10 text-white" : "text-white/50 hover:bg-white/5 hover:text-white/70"}`
                  }
                >
                  <LayoutDashboard className="w-5 h-5" />
                  <span className="font-medium">Projekte</span>
                </NavLink>
                {visibleNavItems.map((item) => (
                  <NavLink
                    key={item.to} to={item.to} onClick={() => setMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-3 rounded ${isActive ? "bg-white/10 text-white" : "text-white/50 hover:bg-white/5 hover:text-white/70"}`
                    }
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="font-medium">{item.label}</span>
                  </NavLink>
                ))}
              </nav>
            </div>
          </div>
        )}
      </div>

      {/* PROJECT SUB-NAVIGATION */}
      {showProjectSubNav && currentProjectId && (
        <ProjectSubNav
          projectId={currentProjectId}
          activeProject={activeProject}
          projects={projects}
          onSelectProject={selectProject}
          user={user}
        />
      )}

      {/* ============ MAIN CONTENT ============ */}
      <main className="flex-1 px-6 lg:px-8 py-6">
        {isFocusRoute ? (
          <div className="max-w-4xl mx-auto">
            <Outlet />
          </div>
        ) : (
          <Outlet />
        )}
      </main>

      {/* Changelog Modal — global, klickbar via Build-Badge in Top-Bar oder Sidebar */}
      <ChangelogModal open={changelogOpen} onClose={() => setChangelogOpen(false)} currentLabel={BUILD_LABEL} />
    </div>
  );
}
